import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { employeesApi, companiesApi } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Home,
  UserX,
  UserPlus,
  RotateCcw,
} from 'lucide-react';
import { motion } from 'framer-motion';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

const StatusBadge = ({ status }) => {
  const styles = {
    homeowner: 'status-homeowner',
    renter: 'status-renter',
    pending: 'status-pending',
  };
  
  const icons = {
    homeowner: <Home className="w-3 h-3" />,
    renter: <XCircle className="w-3 h-3" />,
    pending: <Clock className="w-3 h-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {icons[status] || icons.pending}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inactiveStats, setInactiveStats] = useState(null);
  const [filters, setFilters] = useState({
    company_id: '',
    state: '',
    homeowner_status: '',
    search: '',
    show_inactive: false,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_id: '',
    start_date: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    homeowner_status: 'pending',
  });

  useEffect(() => {
    loadCompanies();
    loadInactiveStats();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await companiesApi.getAll();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadInactiveStats = async () => {
    try {
      const response = await api.get('/employees/stats/inactive');
      setInactiveStats(response.data);
    } catch (error) {
      console.error('Error loading inactive stats:', error);
    }
  };

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.state) params.state = filters.state;
      if (filters.homeowner_status) params.homeowner_status = filters.homeowner_status;
      if (filters.search) params.search = filters.search;
      if (filters.show_inactive) params.show_inactive = true;
      
      const response = await employeesApi.getAll(params);
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setSelectedEmployee(employee);
      setFormData({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        company_id: employee.company_id,
        start_date: employee.start_date?.split('T')[0] || '',
        address: employee.address,
        city: employee.city,
        state: employee.state,
        zip_code: employee.zip_code,
        homeowner_status: employee.homeowner_status,
      });
    } else {
      setSelectedEmployee(null);
      setFormData({
        first_name: '',
        last_name: '',
        company_id: companies[0]?.id || '',
        start_date: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        homeowner_status: 'pending',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEmployee) {
        await employeesApi.update(selectedEmployee.id, formData);
        toast.success('Employee updated successfully');
      } else {
        await employeesApi.create(formData);
        toast.success('Employee created successfully');
      }
      setDialogOpen(false);
      loadEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleDelete = async (employee, permanent = false) => {
    const action = permanent ? 'permanently delete' : 'mark as left';
    if (!window.confirm(`Are you sure you want to ${action} ${employee.first_name} ${employee.last_name}?${permanent ? ' This cannot be undone.' : ''}`)) return;
    try {
      await api.delete(`/employees/${employee.id}`, { params: { permanent } });
      toast.success(permanent ? 'Employee permanently deleted' : 'Employee marked as left');
      loadEmployees();
      loadInactiveStats();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleRestore = async (employee) => {
    try {
      await api.post(`/employees/${employee.id}/restore`);
      toast.success('Employee restored to active status');
      loadEmployees();
      loadInactiveStats();
    } catch (error) {
      toast.error('Failed to restore employee');
    }
  };

  const handleUpdateStatus = async (employee, newStatus) => {
    try {
      await employeesApi.update(employee.id, { homeowner_status: newStatus });
      toast.success('Status updated');
      loadEmployees();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Employees</h1>
          <p className="text-muted-foreground mt-1">
            Manage employee census records
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#E31837] hover:bg-[#c41530] text-white"
          data-testid="add-employee-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Inactive Stats Banner */}
      {inactiveStats && inactiveStats.total_inactive > 0 && (
        <Card className="bg-card border-border border-l-4 border-l-red-500">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <UserX className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-medium">
                    {inactiveStats.total_inactive} employee(s) have left
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {inactiveStats.homeowners} homeowner(s), {inactiveStats.renters} renter(s), {inactiveStats.pending} pending
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show inactive</span>
                <Switch
                  checked={filters.show_inactive}
                  onCheckedChange={(checked) => setFilters({ ...filters, show_inactive: checked })}
                  data-testid="show-inactive-toggle"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or address..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  className="pl-10 bg-background border-input"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select
              value={filters.company_id || 'all'}
              onValueChange={(value) =>
                setFilters({ ...filters, company_id: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="bg-background border-input" data-testid="filter-company">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.state || 'all'}
              onValueChange={(value) =>
                setFilters({ ...filters, state: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="bg-background border-input" data-testid="filter-state">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.homeowner_status || 'all'}
              onValueChange={(value) =>
                setFilters({ ...filters, homeowner_status: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="bg-background border-input" data-testid="filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="homeowner">Homeowner</SelectItem>
                <SelectItem value="renter">Renter</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch
                id="show-inactive"
                checked={filters.show_inactive}
                onCheckedChange={(checked) => setFilters({ ...filters, show_inactive: checked })}
              />
              <Label htmlFor="show-inactive" className="text-sm cursor-pointer">
                {filters.show_inactive ? 'Showing Left' : 'Show Left'}
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="bg-card border-border" data-testid="employees-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : employees.length === 0 ? (
            <div className="empty-state py-12">
              <Users className="w-12 h-12 mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-medium mb-2">No employees found</h3>
              <p className="text-sm text-muted-foreground">
                {filters.search || filters.company_id || filters.state || filters.homeowner_status || filters.show_inactive
                  ? 'Try adjusting your filters'
                  : 'Upload a census file to add employees'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Age</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Start Date</th>
                    <th>Tenure</th>
                    <th>Status</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => {
                    // Handle legacy data - if last_name is empty and name has a space, split it
                    let firstName = employee.first_name || '';
                    let lastName = employee.last_name || '';
                    if (!lastName && employee.name && employee.name.includes(' ')) {
                      const parts = employee.name.split(' ');
                      firstName = parts[0];
                      lastName = parts.slice(1).join(' ');
                    } else if (!firstName && employee.name) {
                      firstName = employee.name;
                    }
                    
                    return (
                    <motion.tr
                      key={employee.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={!employee.is_active ? 'opacity-60' : ''}
                      data-testid={`employee-row-${employee.id}`}
                    >
                      <td className="text-muted-foreground text-xs">
                        {employee.employee_id || '-'}
                      </td>
                      <td>
                        <p className="font-medium">{firstName}</p>
                      </td>
                      <td>
                        <p className="font-medium">{lastName || <span className="text-muted-foreground italic">-</span>}</p>
                      </td>
                      <td className="text-muted-foreground text-sm">
                        {employee.date_of_birth ? (() => {
                          const dob = new Date(employee.date_of_birth);
                          const today = new Date();
                          let age = today.getFullYear() - dob.getFullYear();
                          const m = today.getMonth() - dob.getMonth();
                          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                          return isNaN(age) ? '-' : age;
                        })() : '-'}
                      </td>
                      <td className="text-muted-foreground">
                        {employee.company_name}
                      </td>
                      <td className="text-muted-foreground text-sm">
                        {employee.email || '-'}
                      </td>
                      <td className="text-muted-foreground text-sm">
                        {employee.phone || '-'}
                      </td>
                      <td>
                        <div>
                          <span className="text-muted-foreground">
                            {employee.city}, {employee.state}
                          </span>
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {employee.address}
                          </p>
                        </div>
                      </td>
                      <td className="text-muted-foreground">
                        {employee.start_date ? new Date(employee.start_date).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <span className="text-muted-foreground">
                          {employee.tenure_days} days
                          {employee.tenure_days >= 365 && (
                            <span className="text-xs ml-1">
                              ({Math.floor(employee.tenure_days / 365)}y)
                            </span>
                          )}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={employee.homeowner_status} />
                          {!employee.is_active && (
                            <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400 text-xs">
                              Left
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`employee-menu-${employee.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border">
                            <DropdownMenuItem onClick={() => handleOpenDialog(employee)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {employee.homeowner_status === 'pending' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleUpdateStatus(employee, 'homeowner')}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                                  Mark as Homeowner
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleUpdateStatus(employee, 'renter')}
                                >
                                  <XCircle className="w-4 h-4 mr-2 text-blue-500" />
                                  Mark as Renter
                                </DropdownMenuItem>
                              </>
                            )}
                            {employee.homeowner_status !== 'pending' && (
                              <DropdownMenuItem
                                onClick={() => handleUpdateStatus(employee, 'pending')}
                              >
                                <Clock className="w-4 h-4 mr-2 text-yellow-500" />
                                Set to Pending
                              </DropdownMenuItem>
                            )}
                            {employee.is_active ? (
                              <DropdownMenuItem
                                onClick={() => handleDelete(employee, false)}
                                className="text-orange-500"
                              >
                                <UserX className="w-4 h-4 mr-2" />
                                Mark as Left
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => handleRestore(employee)}
                                className="text-green-500"
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Restore Employee
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDelete(employee, true)}
                              className="text-red-500"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </motion.tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-sm text-muted-foreground">
        Showing {employees.length} {filters.show_inactive ? 'inactive' : 'active'} employee{employees.length !== 1 ? 's' : ''}
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedEmployee ? 'Edit Employee' : 'Add Employee'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  data-testid="employee-first-name-input"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  data-testid="employee-last-name-input"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, company_id: value })
                  }
                >
                  <SelectTrigger className="bg-background border-input" data-testid="employee-company-select">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  data-testid="employee-start-date-input"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                  className="bg-background border-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  data-testid="employee-address-input"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  data-testid="employee-city-input"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  className="bg-background border-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(value) =>
                      setFormData({ ...formData, state: value })
                    }
                  >
                    <SelectTrigger className="bg-background border-input" data-testid="employee-state-select">
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">Zip</Label>
                  <Input
                    id="zip_code"
                    data-testid="employee-zip-input"
                    value={formData.zip_code}
                    onChange={(e) =>
                      setFormData({ ...formData, zip_code: e.target.value })
                    }
                    className="bg-background border-input"
                  />
                </div>
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="homeowner_status">Homeowner Status</Label>
                <Select
                  value={formData.homeowner_status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, homeowner_status: value })
                  }
                >
                  <SelectTrigger className="bg-background border-input" data-testid="employee-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending Verification</SelectItem>
                    <SelectItem value="homeowner">Homeowner</SelectItem>
                    <SelectItem value="renter">Renter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee_id">Employee ID</Label>
                <Input
                  id="employee_id"
                  data-testid="employee-emp-id-input"
                  value={formData.employee_id || ''}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="salary">Salary</Label>
                <Input
                  id="salary"
                  data-testid="employee-salary-input"
                  value={formData.salary || ''}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  data-testid="employee-dob-input"
                  type="date"
                  value={formData.date_of_birth || ''}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="employee-email-input"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="employee-phone-input"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-background border-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#E31837] hover:bg-[#c41530] text-white"
                data-testid="save-employee-button"
              >
                {selectedEmployee ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
